export type PeriodType =
  | "today"
  | "yesterday"
  | "last7"
  | "currentMonth"
  | "lastMonth"
  | "last30"
  | "last60"
  | "custom";

export const PERIOD_OPTIONS: { value: PeriodType; label: string }[] = [
  { value: "today", label: "Hoje" },
  { value: "yesterday", label: "Ontem" },
  { value: "last7", label: "Últimos 7 dias" },
  { value: "currentMonth", label: "Mês atual" },
  { value: "lastMonth", label: "Mês passado" },
  { value: "last30", label: "Últimos 30 dias" },
  { value: "last60", label: "Últimos 60 dias" },
  { value: "custom", label: "Personalizado" },
];

// Período consultado nos relatórios: o tipo (usado pelos mocks da conta
// Preview) e o intervalo de datas já resolvido.
export interface ReportQuery {
  period: PeriodType;
  startDate: Date;
  endDate: Date;
}

const startOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

const addDays = (date: Date, days: number) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

export const getPeriodDateRange = (
  period: Exclude<PeriodType, "custom">,
  now = new Date(),
): { startDate: Date; endDate: Date } => {
  switch (period) {
    case "yesterday": {
      const y = addDays(now, -1);
      return { startDate: startOfDay(y), endDate: endOfDay(y) };
    }
    case "last7":
      return { startDate: startOfDay(addDays(now, -6)), endDate: endOfDay(now) };
    case "currentMonth":
      return {
        startDate: new Date(now.getFullYear(), now.getMonth(), 1),
        endDate: endOfDay(now),
      };
    case "lastMonth":
      return {
        startDate: new Date(now.getFullYear(), now.getMonth() - 1, 1),
        endDate: endOfDay(new Date(now.getFullYear(), now.getMonth(), 0)),
      };
    case "last30":
      return { startDate: startOfDay(addDays(now, -29)), endDate: endOfDay(now) };
    case "last60":
      return { startDate: startOfDay(addDays(now, -59)), endDate: endOfDay(now) };
    case "today":
    default:
      return { startDate: startOfDay(now), endDate: endOfDay(now) };
  }
};

export const toFullDayRange = (startDate: Date, endDate: Date) => ({
  startDate: startOfDay(startDate),
  endDate: endOfDay(endDate),
});

// dd/mm/aaaa
export const formatDateBR = (date: Date): string =>
  date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

export const getPeriodLabel = (query: ReportQuery): string => {
  if (query.period === "custom") {
    return `${formatDateBR(query.startDate)} a ${formatDateBR(query.endDate)}`;
  }
  return PERIOD_OPTIONS.find((o) => o.value === query.period)?.label ?? "";
};

const TIME_ZONE = "America/Sao_Paulo";

const getTimeZoneOffset = (date: Date): string => {
  const part = new Intl.DateTimeFormat("en-US", {
    timeZone: TIME_ZONE,
    timeZoneName: "shortOffset",
  })
    .formatToParts(date)
    .find((p) => p.type === "timeZoneName")?.value;
  const match = part?.match(/GMT([+-]\d{1,2})(?::?(\d{2}))?/);
  if (!match) return "-03:00";

  const hour = Number(match[1]);
  const minute = Number(match[2] || "0");
  const signal = hour >= 0 ? "+" : "-";
  return `${signal}${String(Math.abs(hour)).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

// Formato esperado pela API: 2026-09-23T00:00:00.000-03:00
export const formatDateForAPI = (date: Date): string => {
  const pad = (n: number, size = 2) => String(n).padStart(size, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}` +
    `.${pad(date.getMilliseconds(), 3)}${getTimeZoneOffset(date)}`
  );
};

export const toApiRange = (query: Pick<ReportQuery, "startDate" | "endDate">) => ({
  start: formatDateForAPI(query.startDate),
  end: formatDateForAPI(query.endDate),
});
